import React, { useState } from 'react';
import { Eye, FileText, Download, Calendar, IndianRupeeIcon, AlertCircle, Info, Calculator, Building, CreditCard } from 'lucide-react';

const TaxDocuments = () => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Mock data since no APIs exist for tax documents
  const taxDocuments = [
    {
      id: 1,
      type: 'TDS Certificate',
      period: 'Q3 2024',
      amount: 25000,
      status: 'available',
      generated_date: '2024-01-15'
    },
    {
      id: 2,
      type: 'Investment Declaration',
      period: 'FY 2024-25',
      amount: 150000,
      status: 'submitted',
      generated_date: '2024-04-01'
    },
    {
      id: 3,
      type: 'Tax Computation',
      period: 'FY 2024-25',
      amount: 45000,
      status: 'available',
      generated_date: '2024-03-31'
    }
  ];

  const taxSummary = {
    annual_taxable_income: 1200000,
    total_tax_deducted: 120000,
    investment_declared: 150000,
    tax_saved: 46500,
    professional_tax: 2400
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 4 + i);

  const getStatusColor = (status) => {
    switch (status) {
      case 'available': return 'bg-green-100 text-green-800';
      case 'submitted': return 'bg-blue-100 text-blue-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'expired': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const downloadDocument = (docId, docType) => {
    // Mock download - in real app, this would call an API
    alert(`Downloading ${docType}... (API not implemented)`);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Tax Documents</h1>
          <p className="text-gray-600 mt-2">Access your tax-related documents and declarations</p>
        </div>

        {/* Year Selection */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Calendar className="h-5 w-5 text-blue-500 mr-2" />
              <span className="text-lg font-semibold text-gray-900">Financial Year</span>
            </div>
            <select
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            >
              {years.map(year => (
                <option key={year} value={year}>FY {year}-{year + 1}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Tax Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <IndianRupeeIcon className="h-8 w-8 text-blue-500 mr-3" />
              <div>
                <p className="text-sm text-gray-600">Annual Taxable Income</p>
                <p className="text-xl font-bold text-gray-900">₹{taxSummary.annual_taxable_income.toLocaleString()}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <Calculator className="h-8 w-8 text-red-500 mr-3" />
              <div>
                <p className="text-sm text-gray-600">Total Tax Deducted</p>
                <p className="text-xl font-bold text-gray-900">₹{taxSummary.total_tax_deducted.toLocaleString()}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <Building className="h-8 w-8 text-green-500 mr-3" />
              <div>
                <p className="text-sm text-gray-600">Investment Declared</p>
                <p className="text-xl font-bold text-gray-900">₹{taxSummary.investment_declared.toLocaleString()}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <CreditCard className="h-8 w-8 text-purple-500 mr-3" />
              <div>
                <p className="text-sm text-gray-600">Tax Saved</p>
                <p className="text-xl font-bold text-gray-900">₹{taxSummary.tax_saved.toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Available Documents */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Available Documents</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {taxDocuments.map((document) => (
              <div key={document.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center">
                    <FileText className="h-6 w-6 text-blue-500 mr-2" />
                    <div>
                      <h4 className="font-medium text-gray-900">{document.type}</h4>
                      <p className="text-sm text-gray-600">{document.period}</p>
                    </div>
                  </div>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(document.status)}`}>
                    {document.status}
                  </span>
                </div>
                
                <div className="mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Amount:</span>
                    <span className="font-medium text-gray-900">₹{document.amount.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-gray-600">Generated:</span>
                    <span className="text-gray-900">{new Date(document.generated_date).toLocaleDateString()}</span>
                  </div>
                </div>

                <div className="flex space-x-2">
                  <button
                    onClick={() => downloadDocument(document.id, document.type)}
                    className="flex-1 bg-blue-50 text-blue-700 px-3 py-2 rounded-md hover:bg-blue-100 flex items-center justify-center text-sm font-medium"
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Download
                  </button>
                  <button className="flex-1 bg-gray-50 text-gray-700 px-3 py-2 rounded-md hover:bg-gray-100 flex items-center justify-center text-sm font-medium">
                    <Eye className="h-4 w-4 mr-1" />
                    View
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tax Calculation Breakdown */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Tax Calculation Breakdown</h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-gray-900 mb-3">Income Details</h4>
              <div className="space-y-3 bg-gray-50 rounded-lg p-4">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Gross Annual Salary:</span>
                  <span className="text-sm font-medium text-gray-900">₹{taxSummary.annual_taxable_income.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Professional Tax:</span>
                  <span className="text-sm font-medium text-gray-900">₹{taxSummary.professional_tax.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Standard Deduction:</span>
                  <span className="text-sm font-medium text-gray-900">₹50,000</span>
                </div>
                <div className="flex justify-between border-t border-gray-300 pt-2">
                  <span className="text-sm font-semibold text-gray-900">Taxable Income:</span>
                  <span className="text-sm font-bold text-blue-600">₹{(taxSummary.annual_taxable_income - 50000).toLocaleString()}</span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-medium text-gray-900 mb-3">Tax Savings</h4>
              <div className="space-y-3 bg-gray-50 rounded-lg p-4">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">80C Investments:</span>
                  <span className="text-sm font-medium text-gray-900">₹{taxSummary.investment_declared.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">HRA Exemption:</span>
                  <span className="text-sm font-medium text-gray-900">₹24,000</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Medical Insurance:</span>
                  <span className="text-sm font-medium text-gray-900">₹25,000</span>
                </div>
                <div className="flex justify-between border-t border-gray-300 pt-2">
                  <span className="text-sm font-semibold text-gray-900">Total Tax Saved:</span>
                  <span className="text-sm font-bold text-green-600">₹{taxSummary.tax_saved.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Important Tax Information */}
        {/* <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-yellow-500 mr-3 mt-1 flex-shrink-0" />
            <div>
              <h3 className="text-lg font-semibold text-yellow-900 mb-3">Important Tax Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-yellow-800">
                <div>
                  <p className="font-medium mb-2">Document Availability:</p>
                  <ul className="space-y-1 list-disc list-inside">
                    <li>TDS certificates are generated quarterly</li>
                    <li>Form 16 is available after March 31st</li>
                    <li>Investment proofs are required by February</li>
                    <li>Keep digital copies for 7 years</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium mb-2">Tax Deadlines:</p>
                  <ul className="space-y-1 list-disc list-inside">
                    <li>Investment declaration: January 31st</li>
                    <li>Tax return filing: July 31st</li>
                    <li>Form 16 download: After May 15th</li>
                    <li>Quarterly TDS: Within 15 days of quarter end</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div> */}

        {/* No API Available Notice */}
        {/* <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center">
            <Info className="h-5 w-5 text-red-500 mr-3" />
            <div>
              <h3 className="text-lg font-semibold text-red-900">API Integration Required</h3>
              <p className="text-red-800 mt-2">
                This page displays mock data. The following APIs need to be implemented for full functionality:
              </p>
              <ul className="list-disc list-inside text-red-700 mt-2 text-sm">
                <li><code>GET /api/tax/documents</code> - Fetch tax documents</li>
                <li><code>GET /api/tax/certificates/{id}/download</code> - Download certificates</li>
                <li><code>GET /api/tax/summary</code> - Tax calculation summary</li>
                <li><code>POST /api/tax/investment-declaration</code> - Submit declarations</li>
              </ul>
            </div>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default TaxDocuments;